import { LineChart, Line, ResponsiveContainer, Tooltip, ReferenceArea, YAxis, XAxis } from 'recharts';
import { formatDate } from '../../util/GenericUtil';
import { convertDateToTimestamp, applyMinimumGapConstraint } from '../../util/TimeBasedScaling';
import { parseRangeValue, parseResultValue, computeRangeZones } from '../../util/RangeZones';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                padding: '10px',
                borderRadius: '4px'
            }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {data.testdate ? formatDate(String(data.testdate)) : 'No Date'}
                </p>
                <p style={{ margin: '5px 0 0 0' }}>
                    {data.result}
                </p>
            </div>
        );
    }
    return null;
};

function LinePlot (props) {
    // Extract result values, stripping inequality prefixes like "<9"
    const resultValues = props.test.results.map(r => {
        const raw = typeof r === 'object' ? r.result : r;
        return parseResultValue(raw) ?? 0;
    });

    // Convert testdates to timestamps and apply shared time-based scaling
    const dataWithTimestamps = props.test.results.map(r => ({
        ...r,
        timestamp: convertDateToTimestamp(String(r.testdate || r.date || '')),
        result: parseResultValue(typeof r === 'object' ? r.result : r) ?? 0
    })).sort((a, b) => a.timestamp - b.timestamp);

    const adjustedData = applyMinimumGapConstraint(dataWithTimestamps);

    // Compute y-axis domain from result values only — ranges never affect the axis
    const dataMin = Math.min(...resultValues);
    const dataMax = Math.max(...resultValues);
    const span = dataMax - dataMin;
    const yPad = span > 0 ? span * 0.1 : (dataMin === 0 ? 0.5 : Math.abs(dataMin) * 0.1);
    const yMin = dataMin - yPad;
    const yMax = dataMax + yPad;

    // Compute range zones using shared helper (mirrors backend get_marker_direction)
    const zones = computeRangeZones({
        fxLow: parseRangeValue(props.fxRange?.fx_low),
        fxHigh: parseRangeValue(props.fxRange?.fx_high),
        labLow: parseRangeValue(props.labLow),
        labHigh: parseRangeValue(props.labHigh),
        domainMin: yMin,
        domainMax: yMax,
    });

    // Expand x-axis domain so ReferenceArea fills full chart width
    const timestamps = adjustedData.map(d => d.adjustedTimestamp);
    const xMin = timestamps.length > 0 ? Math.min(...timestamps) : 0;
    const xMax = timestamps.length > 0 ? Math.max(...timestamps) : 1;
    const xSpan = xMax - xMin;
    const xBuffer = xSpan > 0 ? xSpan * 0.02 : 86400000;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={adjustedData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <XAxis
                        dataKey="adjustedTimestamp"
                        type="number"
                        domain={[xMin - xBuffer, xMax + xBuffer]}
                        hide={true}
                        height={0}
                    />
                    <YAxis domain={[yMin, yMax]} hide={true} width={0} />
                    {zones.abnormalLow && <ReferenceArea y1={zones.abnormalLow.y1} y2={zones.abnormalLow.y2} fill="#555" fillOpacity={0.2} />}
                    {zones.abnormalHigh && <ReferenceArea y1={zones.abnormalHigh.y1} y2={zones.abnormalHigh.y2} fill="#555" fillOpacity={0.2} />}
                    {zones.dysfunctionalLow && <ReferenceArea y1={zones.dysfunctionalLow.y1} y2={zones.dysfunctionalLow.y2} fill="#888" fillOpacity={0.15} />}
                    {zones.dysfunctionalHigh && <ReferenceArea y1={zones.dysfunctionalHigh.y1} y2={zones.dysfunctionalHigh.y2} fill="#888" fillOpacity={0.15} />}
                    {zones.optimal && <ReferenceArea y1={zones.optimal.y1} y2={zones.optimal.y2} fill="green" fillOpacity={0.2} />}
                    <Line type="monotone" dataKey="result" stroke="rgb(47, 180, 103)" strokeWidth={2} />
                    <Tooltip content={<CustomTooltip />} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default LinePlot;
